import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";
import ProjectCard from "../components/ProjectCard";
import PhotoGallery from "../components/PhotoGallery";
import Reveal, { Eyebrow } from "../components/Reveal";
import { PROJECT_CATEGORIES } from "../constants/content";

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const filtered = projects ? (cat === "all" ? projects : projects.filter((p) => p.category === cat)) : [];

  return (
    <>
      <Seo title="Our Projects | Wellington Renovations, Extensions & New Builds — Razor Build" description="Completed Razor Build projects across the Wellington region — renovations, extensions, new builds, bathrooms, kitchens and outdoor work, with before, during and completed photos." />
      <section className="pt-32 md:pt-40 pb-14 md:pb-20 bg-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Eyebrow light>Our Projects</Eyebrow>
          <h1 className="mt-4 font-display font-bold text-4xl md:text-6xl tracking-tight text-white display-expanded leading-[1.0]">
            The Work Speaks<br />for Itself.
          </h1>
          <p className="mt-5 text-white/70 max-w-xl leading-relaxed">
            Renovations, extensions and new builds from across the Wellington region — with before, during and completed photos so you can see how each project came together.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-paper min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div data-testid="project-filters" className="flex flex-wrap gap-2 mb-10">
            {PROJECT_CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCat(c.slug)}
                data-testid={`filter-${c.slug}`}
                className={`font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 border transition-colors ${cat === c.slug ? "bg-ink text-white border-ink" : "border-line text-slate2 hover:border-ink hover:text-ink bg-white"}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {projects === null ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] bg-line/50 animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="projects-page-empty" className="border border-line bg-white px-8 py-16 text-center">
              <img src="/logo-mark-black.png" alt="" className="w-12 mx-auto opacity-25 mb-6" />
              <h2 className="font-display font-semibold text-xl text-ink">
                {cat === "all" ? "Our project gallery is being prepared" : `No ${PROJECT_CATEGORIES.find((c) => c.slug === cat)?.label.toLowerCase()} projects listed yet`}
              </h2>
              <p className="mt-3 text-slate2 text-sm max-w-md mx-auto leading-relaxed">
                We're photographing recent work across the region now. Talk to us about your project and we can walk you through relevant past jobs.
              </p>
              <Link to="/contact" data-testid="projects-page-cta" className="mt-7 inline-flex items-center gap-2.5 bg-ink hover:bg-charcoal text-white font-mono text-[11px] tracking-[0.18em] uppercase px-6 py-3.5 transition-colors">
                Start Your Project <ArrowRight className="w-4 h-4 text-gold" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.06}>
                  <ProjectCard project={p} index={i} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
      <PhotoGallery />
    </>
  );
}
